import type { BroadcastDeliveryState } from "@/components/contact-query-builder/types";
import type { BroadcastMessageFilter } from "@/features/reports/broadcasts/types";

import {
  BroadcastFilter,
  BroadcastPauseCategory,
  BroadcastStatus,
} from "./types";

// React Query Cache Keys
export const BROADCAST_CACHE_KEY = "BROADCAST";

export const ALL_BROADCAST_CACHE_KEY = (filters: BroadcastFilter = {}) => [
  BROADCAST_CACHE_KEY,
  "ALL",
  { filters },
];

export const BROADCAST_DETAIL_CACHE_KEY = (id: string) => [
  BROADCAST_CACHE_KEY,
  "DETAIL",
  id,
];

export const BROADCAST_PREVIEW_CONTACTS_CACHE_KEY = (
  segmentId: string,
  channelId: string,
  excludeNotInteracted: boolean,
) => [
  BROADCAST_CACHE_KEY,
  "PREVIEW_CONTACTS",
  segmentId,
  channelId,
  excludeNotInteracted,
];

export const BROADCAST_PREVIEW_BILLING_CACHE_KEY = (
  segmentId: string,
  channelId: string,
  templateId: string,
  interactedOnly: boolean,
) => [
  BROADCAST_CACHE_KEY,
  "PREVIEW_BILLING",
  segmentId,
  channelId,
  templateId,
  interactedOnly,
];

// Broadcast Status Configuration
export const BROADCAST_STATUS = {
  PENDING: "pending",
  SCHEDULED: "scheduled",
  PROCESSING: "processing",
  SENDING: "sending",
  PAUSED: "paused",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  FAILED: "failed",
} as const;

export const BROADCAST_STATUS_CONFIG: Record<
  BroadcastStatus,
  {
    label: string;
    color: "default" | "accent" | "success" | "warning" | "danger";
  }
> = {
  pending: { label: "Pending", color: "default" },
  scheduled: { label: "Scheduled", color: "accent" },
  processing: { label: "Processing", color: "warning" },
  sending: { label: "Sending", color: "default" },
  paused: { label: "Paused", color: "warning" },
  completed: { label: "Completed", color: "success" },
  cancelled: { label: "Cancelled", color: "danger" },
  failed: { label: "Failed", color: "danger" },
};

export const EDITABLE_STATUSES: BroadcastStatus[] = [
  "pending",
  "scheduled",
  "sending",
  "processing",
  "paused",
];
export const DELETABLE_STATUSES: BroadcastStatus[] = ["scheduled", "cancelled"];
export const CANCELLABLE_STATUSES: BroadcastStatus[] = [
  "pending",
  "scheduled",
  "processing",
  "sending",
  "paused",
];
export const PAUSABLE_STATUSES: BroadcastStatus[] = ["processing", "sending"];
export const RESUMABLE_STATUSES: BroadcastStatus[] = ["paused"];

export const BROADCAST_PAUSE_CATEGORY = {
  USER_ACTION: "user_action",
  OUTSIDE_ACTIVE_HOURS: "outside_active_hours",
} as const;

export const BROADCAST_PAUSE_CATEGORY_CONFIG: Record<
  BroadcastPauseCategory,
  { label: string }
> = {
  user_action: { label: "Paused manually" },
  outside_active_hours: { label: "Outside active hours" },
};

/** The people filters a segment rule can say; the rest have no Save as segment. */
export const BROADCAST_FILTER_SEGMENT_STATUS: Partial<
  Record<BroadcastMessageFilter, BroadcastDeliveryState>
> = {
  all: "any",
  sent: "received",
  delivered: "delivered",
  read: "read",
  not_sent: "notSent",
};

export const CONTACT_FIELD_OPTIONS = [
  { key: "firstName", label: "First name" },
  { key: "lastName", label: "Last name" },
  { key: "fullName", label: "Full name" },
  { key: "phoneNumber", label: "Phone number" },
] as const;
